import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@/ui';

import CancellationEvidenceList from '../CancellationEvidenceList';
import ScheduleAppointmentCard from '../ScheduleAppointmentCard';
import ScheduleSessionCard from '../ScheduleSessionCard';
import { dayKeyFromIso, formatDateKey, formatIsoTime } from '../calendarGrid';
import { canCancelSession, canRescheduleSession } from '../sessionData';
import { useSessionActions } from '../useSessionActions';

import type { ScheduleListRow } from '@/features/platformScheduleList/types';
import type { Session } from '@/types';
import type { FC } from 'react';

import {
  actionRecordCanCancel,
  actionRecordCanReschedule,
} from '@/features/appointments/appointmentActionRecord';
import {
  participantCalendarAppointmentRecord,
  platformAdminCalendarAppointmentRecord,
} from '@/features/appointments/calendarAppointmentProjection';
import { legacyAppSelectedPair } from '@/features/appointments/momentsSelectedPairContract';
import { usePlatformScheduleList } from '@/features/platformScheduleList/usePlatformScheduleList';
import { useGetMomentsSelectedPairOperationsQuery } from '@/store/api/momentsAppointmentApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

const TODAY_ANCHOR_ID = 'schedule-today-anchor';
const MENU_ITEM_HEIGHT_PX = 41;
const MENU_VERTICAL_PADDING_PX = 16;

const rescheduleScopeLabel = (scope: string | null) => {
  if (scope === 'this_and_following') {
    return 'Selected appointment and following';
  }
  if (scope === 'appointment') return 'Selected appointment';
  return 'Legacy reschedule';
};

// MEN-5421: schedules are RFC 3339 instants; show them on the wall clock of
// the zone the label names.
const scheduleLabel = (
  label: string,
  schedule: { endAt: string; startAt: string; timezone: string },
) =>
  `${label}: ${formatDateKey(dayKeyFromIso(schedule.startAt, schedule.timezone), { weekday: true })} ${formatIsoTime(schedule.startAt, schedule.timezone)}-${formatIsoTime(schedule.endAt, schedule.timezone)} ${schedule.timezone}`;

interface SelectedPairListViewProps {
  adminCoachId?: string;
  coachId: string;
  hideCancelled?: boolean;
  memberId: string;
  timeZone?: string | null;
}

interface RowGroup {
  header: string;
  isToday: boolean;
  key: string;
  rows: ScheduleListRow[];
}

type AppointmentRow = Exclude<ScheduleListRow, { source: 'rails-session' }>;

const rowActionRecord = (row: AppointmentRow) => {
  const suppliedRecord = (
    row as {
      actionRecord?: ReturnType<typeof participantCalendarAppointmentRecord>;
    }
  ).actionRecord;
  if (suppliedRecord) return suppliedRecord;
  const { appointment } = row as { appointment: unknown };
  return row.source === 'platform-admin-appointment'
    ? platformAdminCalendarAppointmentRecord(
        appointment as Parameters<
          typeof platformAdminCalendarAppointmentRecord
        >[0],
      )
    : participantCalendarAppointmentRecord(
        appointment as Parameters<
          typeof participantCalendarAppointmentRecord
        >[0],
      );
};

const rowStartAt = (row: ScheduleListRow): string =>
  row.source === 'rails-session'
    ? row.session.startedAt
    : rowActionRecord(row).view.currentSchedule.startAt;

const dayHeader = (isoDate: string, todayKey: string): string => {
  if (isoDate === todayKey) return `Today · ${formatDateKey(isoDate)}`;
  return formatDateKey(isoDate, { weekday: true });
};

const groupByDay = (
  rows: ScheduleListRow[],
  timeZone: string,
  todayKey: string,
): RowGroup[] => {
  const byDay = new Map<string, ScheduleListRow[]>();
  rows.forEach((row) => {
    const key = dayKeyFromIso(rowStartAt(row), timeZone);
    const bucket = byDay.get(key) ?? [];
    bucket.push(row);
    byDay.set(key, bucket);
  });

  return [...byDay.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, groupedRows]) => ({
      header: dayHeader(key, todayKey),
      isToday: key === todayKey,
      key,
      rows: groupedRows,
    }));
};

const rowSession = (row: ScheduleListRow): Session => {
  if (row.source === 'rails-session') return row.session;
  if ('displaySession' in row) return row.displaySession;
  const { pairedSession } = row as {
    pairedSession?: Session;
  };
  return pairedSession ?? rowActionRecord(row).session;
};

const rowEndAt = (row: ScheduleListRow): string =>
  row.source === 'rails-session'
    ? row.session.endedAt
    : rowActionRecord(row).view.currentSchedule.endAt;

const rowCanReschedule = (
  row: ScheduleListRow | undefined,
  adminMode: boolean,
): boolean =>
  row && row.source !== 'rails-session'
    ? actionRecordCanReschedule(rowActionRecord(row))
    : Boolean(row && canRescheduleSession(row.session, adminMode));

const rowCanCancel = (
  row: ScheduleListRow | undefined,
  adminMode: boolean,
): boolean =>
  row && row.source !== 'rails-session'
    ? actionRecordCanCancel(rowActionRecord(row))
    : Boolean(row && canCancelSession(row.session, adminMode));

const rowIsMenuReadOnly = (
  row: ScheduleListRow | undefined,
  adminMode: boolean,
): boolean => {
  if (!row) return false;
  return (
    new Date(rowEndAt(row)).getTime() < Date.now() ||
    (!rowCanReschedule(row, adminMode) && !rowCanCancel(row, adminMode))
  );
};

const rowHasMenuItems = (row: ScheduleListRow, adminMode: boolean): boolean =>
  rowIsMenuReadOnly(row, adminMode)
    ? true
    : rowCanReschedule(row, adminMode) || rowCanCancel(row, adminMode);

const sessionMenuItemCount = (
  session: Session | null,
  hasProfile: boolean,
  readOnly: boolean,
  canReschedule: boolean,
  canCancel: boolean,
): number =>
  readOnly
    ? Number(hasProfile) + Number(Boolean(session?.user.cometChatId))
    : Number(canReschedule) + Number(canCancel);

const SelectedPairListView: FC<SelectedPairListViewProps> = ({
  adminCoachId,
  coachId,
  hideCancelled,
  memberId,
  timeZone: nullableTimeZone,
}) => {
  const timeZone = nullableTimeZone ?? 'UTC';
  const {
    cancellationEvidence = [],
    hasMoreFuture,
    hasMoreEvidence,
    hasMorePast,
    evidenceLoadError,
    evidenceUnavailable,
    isLoadingMoreEvidence,
    loadMoreEvidence,
    loadMoreFuture,
    loadMorePast,
    refetch,
    rows,
    status,
  } = usePlatformScheduleList({
    adminCoachId,
    coachId,
    hideCancelled,
    memberId,
    timeZone,
  });
  const evidenceScopeKey = `${coachId}:${memberId}`;
  const [evidencePage, setEvidencePage] = useState<{
    cursor?: string;
    scopeKey: string;
  }>({ scopeKey: evidenceScopeKey });
  const evidenceCursor =
    evidencePage.scopeKey === evidenceScopeKey
      ? evidencePage.cursor
      : undefined;
  const evidence = useGetMomentsSelectedPairOperationsQuery(
    adminCoachId
      ? {
          ...legacyAppSelectedPair(adminCoachId, memberId),
          ...(evidenceCursor ? { cursor: evidenceCursor } : {}),
          limit: 25,
        }
      : skipToken,
  );
  const pastSentinelRef = useRef<HTMLDivElement | null>(null);
  const futureSentinelRef = useRef<HTMLDivElement | null>(null);
  const scrolledScopeRef = useRef<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeRowKey, setActiveRowKey] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const actions = useSessionActions(timeZone, { adminCoachId });
  const adminMode = Boolean(adminCoachId);
  const anchor = useMemo(
    () => dayKeyFromIso(new Date().toISOString(), timeZone),
    [timeZone],
  );
  const scopeKey = `${coachId}:${memberId}:${timeZone}`;
  const pastRows = rows.filter(
    (row) => dayKeyFromIso(rowStartAt(row), timeZone) < anchor,
  );
  const todayRows = rows.filter(
    (row) => dayKeyFromIso(rowStartAt(row), timeZone) === anchor,
  );
  const futureRows = rows.filter(
    (row) => dayKeyFromIso(rowStartAt(row), timeZone) > anchor,
  );
  const pastGroups = groupByDay(pastRows, timeZone, anchor);
  const futureGroups = groupByDay(futureRows, timeZone, anchor);

  const sessionActionsMenuId = 'schedule-session-actions-menu';
  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveRowKey(null);
  };
  const openMenu = (rowKey: string, element: HTMLElement) => {
    setActiveRowKey(rowKey);
    setMenuAnchor(element);
  };
  const activeRow = rows.find(({ key }) => key === activeRowKey);
  const activeSession = activeRow ? rowSession(activeRow) : null;
  const activeActionSubject =
    activeRow && activeRow.source !== 'rails-session'
      ? rowActionRecord(activeRow)
      : activeRow?.session;
  const activeIsReadOnly = rowIsMenuReadOnly(activeRow, adminMode);
  const activeCanReschedule = rowCanReschedule(activeRow, adminMode);
  const activeCanCancel = rowCanCancel(activeRow, adminMode);
  const activeMenuItemCount = sessionMenuItemCount(
    activeSession,
    Boolean(activeRow),
    activeIsReadOnly,
    activeCanReschedule,
    activeCanCancel,
  );
  const menuHeight =
    MENU_VERTICAL_PADDING_PX + MENU_ITEM_HEIGHT_PX * activeMenuItemCount;

  useEffect(() => {
    if (status !== 'ready' || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target === pastSentinelRef.current && hasMorePast) {
          loadMorePast();
        }
        if (entry.target === futureSentinelRef.current && hasMoreFuture) {
          loadMoreFuture();
        }
      });
    });

    const pastNode = pastSentinelRef.current;
    const futureNode = futureSentinelRef.current;
    if (pastNode) observer.observe(pastNode);
    if (futureNode) observer.observe(futureNode);
    return () => observer.disconnect();
  }, [hasMoreFuture, hasMorePast, loadMoreFuture, loadMorePast, status]);

  useEffect(() => {
    if (status === 'loading' || scrolledScopeRef.current === scopeKey) return;
    const todayAnchor = document.getElementById(TODAY_ANCHOR_ID);
    if (typeof todayAnchor?.scrollIntoView === 'function') {
      todayAnchor.scrollIntoView({ block: 'start' });
    }
    scrolledScopeRef.current = scopeKey;
  }, [scopeKey, status]);

  if (status === 'loading') {
    return <CircularProgress data-testid="schedule-list-loading" />;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-start gap-2" role="alert">
        <Typography color="textSecondary" variant="body2">
          Appointment schedule is unavailable. Try again.
        </Typography>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  const renderRow = (row: ScheduleListRow) => {
    if (row.source !== 'rails-session') {
      if (rowActionRecord(row).view.lifecycleStatus === 'scheduled') {
        return (
          <ScheduleSessionCard
            key={row.key}
            hasOptions={rowHasMenuItems(row, adminMode)}
            menuId={sessionActionsMenuId}
            menuOpen={activeRowKey === row.key}
            profileMemberId={memberId}
            session={row.displaySession}
            timeZone={timeZone}
            onOpenMenu={(_, element) => openMenu(row.key, element)}
          />
        );
      }
      return (
        <ScheduleAppointmentCard key={row.key} row={row} timeZone={timeZone} />
      );
    }
    return (
      <ScheduleSessionCard
        key={row.key}
        hasOptions={rowHasMenuItems(row, adminMode)}
        menuId={sessionActionsMenuId}
        menuOpen={activeRowKey === row.key}
        profileMemberId={memberId}
        session={row.session}
        timeZone={timeZone}
        onOpenMenu={(_, element) => openMenu(row.key, element)}
      />
    );
  };

  const renderGroup = (group: RowGroup) => (
    <section
      key={group.key}
      className="grid gap-4"
      style={{ gridTemplateColumns: '7.5rem minmax(0, 1fr)' }}
    >
      <div className="py-3" data-testid="schedule-list-date-gutter">
        <Typography variant="subtitle2">{group.header}</Typography>
      </div>
      <div
        className="flex min-w-0 flex-col gap-2"
        data-testid="schedule-list-card-column"
      >
        {group.rows.map(renderRow)}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col gap-3" data-testid="schedule-list-view">
      <div ref={pastSentinelRef} data-testid="schedule-list-past-sentinel" />
      {pastGroups.map(renderGroup)}

      {todayRows.length > 0 ? (
        <section
          className="grid gap-4"
          id={TODAY_ANCHOR_ID}
          style={{ gridTemplateColumns: '7.5rem minmax(0, 1fr)' }}
        >
          <div className="py-3" data-testid="schedule-list-date-gutter">
            <Typography variant="subtitle2">
              {dayHeader(anchor, anchor)}
            </Typography>
          </div>
          <div
            className="flex min-w-0 flex-col gap-2"
            data-testid="schedule-list-card-column"
          >
            {todayRows.map(renderRow)}
          </div>
        </section>
      ) : (
        <div id={TODAY_ANCHOR_ID} />
      )}

      {rows.length === 0 && (
        <Typography color="textSecondary" variant="body2">
          No sessions to show.
        </Typography>
      )}

      {futureGroups.map(renderGroup)}
      <div
        ref={futureSentinelRef}
        data-testid="schedule-list-future-sentinel"
      />

      {adminMode && evidenceUnavailable && (
        <Alert severity="error">
          Cancellation evidence is unavailable for this pair. Appointment
          schedule remains available.
        </Alert>
      )}

      {adminMode && cancellationEvidence.length > 0 && (
        <CancellationEvidenceList evidence={cancellationEvidence} />
      )}

      {adminMode && evidenceLoadError && (
        <Typography color="error" role="alert" variant="body2">
          More cancellation evidence could not be loaded. Earlier evidence is
          still shown.
        </Typography>
      )}
      {adminMode && hasMoreEvidence && (
        <Button
          disabled={isLoadingMoreEvidence}
          variant="outlined"
          onClick={loadMoreEvidence}
        >
          {isLoadingMoreEvidence
            ? 'Loading evidence...'
            : 'Load more cancellation evidence'}
        </Button>
      )}

      {adminCoachId && (
        <section
          aria-labelledby="reschedule-history-heading"
          className="mt-6 flex flex-col gap-3 border-t border-cement-200 pt-5"
        >
          <Typography id="reschedule-history-heading" variant="h5">
            Reschedule history
          </Typography>
          {evidence.isLoading && <CircularProgress />}
          {evidence.isError && !evidence.currentData && (
            <Alert severity="error">
              Reschedule history is unavailable. Try again.
            </Alert>
          )}
          {evidence.currentData?.operations.map((operation) => (
            <article
              key={operation.operationId}
              className="rounded-lg border border-cement-200 bg-white p-4"
            >
              <Typography className="font-medium">
                {rescheduleScopeLabel(operation.scope)}
              </Typography>
              {!operation.evidenceAvailable ? (
                <Typography color="textSecondary">
                  Operation evidence unavailable for this legacy record.
                </Typography>
              ) : (
                <>
                  <Typography>{`Status: ${operation.status.replaceAll('_', ' ')}`}</Typography>
                  <Typography>{`Phase: ${operation.phase.replaceAll('_', ' ')}`}</Typography>
                  <Typography>{`Operation: ${operation.operationId}`}</Typography>
                  <Typography>{`Actor: ${operation.actor?.role ?? 'Unavailable'}`}</Typography>
                  <Typography>{`Requested: ${formatDateKey(dayKeyFromIso(operation.requestedAt, timeZone), { weekday: true })}`}</Typography>
                  <Typography>{`Affected appointments: ${operation.affectedCount ?? 'Unavailable'}`}</Typography>
                  <Typography>{`Affected range: ${operation.affectedStartAt ?? 'Unavailable'} to ${operation.affectedEndAt ?? 'Unavailable'}`}</Typography>
                  {operation.originalSchedules.map((schedule) => (
                    <Typography key={`original-${schedule.startAt}`}>
                      {scheduleLabel('Original', schedule)}
                    </Typography>
                  ))}
                  {operation.proposedSchedules.map((schedule) => (
                    <Typography key={`proposed-${schedule.startAt}`}>
                      {scheduleLabel('Proposed', schedule)}
                    </Typography>
                  ))}
                  {operation.timing && (
                    <Typography>{`Timing: ${operation.timing.classification.replaceAll('_', ' ')}`}</Typography>
                  )}
                  <Typography>{`Busy conflicts acknowledged: ${operation.busyConflictsAcknowledged ? 'Yes' : 'No'}`}</Typography>
                  <Typography>{`Near-term acknowledged: ${operation.nearTermAcknowledged ? 'Yes' : 'No'}`}</Typography>
                  {operation.conflicts.map((conflict) => (
                    <Typography key={conflict.class}>
                      {`${conflict.class.replaceAll('_', ' ')} (${conflict.disposition})${
                        conflict.maskedAccounts.length > 0
                          ? `: ${conflict.maskedAccounts.join(', ')}`
                          : ''
                      }`}
                    </Typography>
                  ))}
                  {operation.reason && (
                    <Alert severity="warning">{operation.reason}</Alert>
                  )}
                  {operation.terminalResult && (
                    <Typography>{`Terminal result: ${operation.terminalResult.status}; ${operation.terminalResult.appointments.length} appointments`}</Typography>
                  )}
                </>
              )}
            </article>
          ))}
          {(evidence.isError || evidence.currentData?.paginationError) &&
            evidence.currentData && (
              <Alert severity="error">
                More reschedule history could not be loaded. Earlier results are
                still shown.
              </Alert>
            )}
          {evidence.currentData?.nextCursor &&
            !evidence.currentData.paginationError && (
              <Button
                disabled={evidence.isFetching}
                variant="outlined"
                onClick={() =>
                  setEvidencePage({
                    cursor: evidence.currentData?.nextCursor ?? undefined,
                    scopeKey: evidenceScopeKey,
                  })
                }
              >
                {evidence.isFetching ? 'Loading more...' : 'Load more history'}
              </Button>
            )}
          {rows.some(({ source }) => source === 'rails-session') && (
            <Typography color="textSecondary" variant="body2">
              Reschedule operation evidence is unavailable for legacy Sessions.
            </Typography>
          )}
        </section>
      )}

      <Menu
        anchorEl={menuAnchor}
        MenuListProps={{ id: sessionActionsMenuId }}
        open={Boolean(menuAnchor && activeRow)}
        PaperProps={{ style: { height: `${menuHeight}px`, width: '155px' } }}
        onClose={closeMenu}
      >
        {activeIsReadOnly
          ? [
              activeRow ? (
                <MenuItem
                  key="profile"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    navigate(`/coach/members/${memberId}`);
                    closeMenu();
                  }}
                >
                  View Profile
                </MenuItem>
              ) : null,
              activeSession?.user.cometChatId ? (
                <MenuItem
                  key="message"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    dispatch(setChatId(activeSession.user.cometChatId));
                    closeMenu();
                  }}
                >
                  {`Message ${activeSession.user.fullName}`.trim()}
                </MenuItem>
              ) : null,
            ]
          : [
              activeCanReschedule ? (
                <MenuItem
                  key="reschedule"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    const focusTarget = menuAnchor;
                    if (activeActionSubject) {
                      actions.openReschedule(activeActionSubject, () =>
                        focusTarget?.focus(),
                      );
                    }
                    closeMenu();
                  }}
                >
                  Reschedule
                </MenuItem>
              ) : null,
              activeCanCancel ? (
                <MenuItem
                  key="cancel"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    if (activeActionSubject) {
                      const focusTarget = menuAnchor;
                      actions
                        .openCancel(activeActionSubject, () =>
                          focusTarget?.focus(),
                        )
                        .catch(() => undefined);
                    }
                    closeMenu();
                  }}
                >
                  Cancel
                </MenuItem>
              ) : null,
            ]}
      </Menu>
      {actions.modals}
    </div>
  );
};

export default SelectedPairListView;
